"use client";

import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DateRangePicker from "./DateRangePicker";
import GenericAutoComplete from "@/components/GenericAutoComplete";
import DriverAgeSelect from "@/components/pages/car/searchForm/DriverAgeSelect";
import { Checkbox } from "@/components/ui/input";
import { cdnCommonPath } from "@/constants/cdnUrls";
import { useCarSearch } from "@/hooks/useCarSearch";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { getPathname } from "@/i18n/navigation";
import { isBefore, isSameDay } from "@/utils/dateFormatter";

const LOCATION_ICONS = {
  0: "plane-departure",
  1: "bus-simple",
  2: "house-building",
  3: "city",
  4: "mountain-city",
  5: "train-subway-tunnel",
};

const Form = ({
  initialPickUpLocationCode,
  initialDropOffLocationCode,
  initialPickUpLocation,
  initialDropOffLocation,
  initialPickUpDate,
  initialDropOffDate,
  initialPickUpTime,
  initialDropOffTime,
  initialDriverAge,
  initialDefaultOriginEmpty,
  className,
  id,
  onSearch,
  webDatePickerDropdownClassName = "",
  scrollToForm,
  defaultFilters = {},
  viewportHeightWrapperActive = false,
} = {}) => {
  const t = useTranslationDetector();
  const tForm = useTranslationDetector();
  const [isDropOffSectionAnimating, setIsDropOffSectionAnimating] =
    useState(false);
  const [shouldRenderDropOffSection, setShouldRenderDropOffSection] =
    useState(false);

  // Hotel-specific storage key
  const STORAGE_KEY = "latestCarSearches";

  // Initial data objesini oluştur (flight search ile aynı mantık)
  const initialData = {};
  if (initialPickUpLocationCode)
    initialData.initialPickUpLocationCode = initialPickUpLocationCode;
  if (initialDropOffLocationCode)
    initialData.initialDropOffLocationCode = initialDropOffLocationCode;
  if (initialPickUpLocation)
    initialData.initialPickUpLocation = initialPickUpLocation;
  if (initialDropOffLocation)
    initialData.initialDropOffLocation = initialDropOffLocation;
  if (
    initialDropOffLocationCode &&
    initialPickUpLocationCode !== initialDropOffLocationCode
  )
    initialData.isDifferentLocation = true;
  if (
    initialDropOffLocation &&
    initialPickUpLocation.Id !== initialDropOffLocation.Id
  )
    initialData.isDifferentLocation = true;
  if (initialPickUpDate) initialData.pickUpDate = initialPickUpDate;
  if (initialDropOffDate) initialData.dropOffDate = initialDropOffDate;
  if (initialPickUpTime) initialData.pickUpTime = initialPickUpTime;
  if (initialDropOffTime) initialData.dropOffTime = initialDropOffTime;
  if (initialDriverAge) initialData.driverAge = initialDriverAge;
  if (initialDefaultOriginEmpty)
    initialData.defaultOriginEmpty = initialDefaultOriginEmpty;
  initialData.storageKey = STORAGE_KEY;

  // Use hotel search hook
  const {
    driverAge,
    pickUpLocation,
    dropOffLocation,
    pickUpDate,
    dropOffDate,
    pickUpTime,
    dropOffTime,
    isDifferentLocation,
    latestCarSearches,
    popularLocations,
    publicHolidays,
    ensurePopularLoaded,
    setPickUpDate,
    setDropOffDate,
    setPickUpTime,
    setDropOffTime,
    setDriverAge,
    setIsDifferentLocation,
    onSelectPickUpLocation,
    onSelectDropOffLocation,
    validateForm,
    generateCarSearchUrl,
    searchForCarLocations,
    refreshPublicHolidays,
  } = useCarSearch({ ...initialData, defaultFilters });

  const locale = useLocale();
  const isMobile = useIsMobile();
  const [validationErrors, setValidationErrors] = useState({});
  const dateSelectionRef = useRef(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Herhangi bir portal açıksa body scroll'u kilitle
  const shouldLockBodyScroll = isMobile && isDatePickerOpen;
  useLockBodyScroll(shouldLockBodyScroll);

  const handlePickUpLocationSelect = useCallback(
    (location) => {
      onSelectPickUpLocation(location);
      setValidationErrors((prev) => ({ ...prev, pickUpLocation: null }));
    },
    [onSelectPickUpLocation],
  );

  const handleDropOffLocationSelect = useCallback(
    (location) => {
      onSelectDropOffLocation(location);
      setValidationErrors((prev) => ({ ...prev, pickUpLocation: null }));
    },
    [onSelectDropOffLocation],
  );

  const clearPickUpLocationSelection = useCallback(() => {
    onSelectPickUpLocation(null);
  }, [onSelectPickUpLocation]);

  const clearDropOffLocationSelection = useCallback(() => {
    onSelectDropOffLocation(null);
  }, [onSelectDropOffLocation]);

  const handleDriverAgeSelect = useCallback(
    (value) => {
      setDriverAge(value);
    },
    [setDriverAge],
  );

  // Handle search history item selection
  const handleSearchHistorySelect = useCallback(
    (search, locationType, onClosePortal) => {
      // Fill all form fields with the selected search data
      const typeMapping = {
        pickUpLocation: onSelectPickUpLocation,
        dropOffLocation: onSelectDropOffLocation,
      };

      if (search.location) {
        typeMapping[locationType](search.location);
      }

      // Clear any validation errors
      setValidationErrors({});

      if (onClosePortal) {
        onClosePortal();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onSelectPickUpLocation,
      onSelectDropOffLocation,
      setPickUpTime,
      setDropOffTime,
      setDropOffDate,
      setPickUpDate,
      handleDriverAgeSelect,
    ],
  );

  const onSelectDropOffDate = useCallback(
    (date, triggerDate) => {
      if (
        pickUpDate &&
        dropOffDate &&
        (isSameDay(triggerDate, pickUpDate) ||
          isSameDay(triggerDate, dropOffDate))
      ) {
        setPickUpDate(triggerDate);
        setDropOffDate(null);
        return;
      }

      if (pickUpDate && !dropOffDate) {
        if (!triggerDate) {
          const to = new Date(pickUpDate);
          to.setDate(to.getDate() + 1);
          setDropOffDate(to);

          if (!isMobile) {
            dateSelectionRef.current?.close();
          }

          return;
        }

        if (isBefore(triggerDate, pickUpDate)) {
          setPickUpDate(triggerDate);
          setDropOffDate(null);

          return;
        }
      }

      if (!pickUpDate) {
        // İlk seçim
        setPickUpDate(triggerDate);
        setDropOffDate(null);
      } else if (!dropOffDate) {
        // İkinci seçim - return date belirleniyor
        if (isBefore(triggerDate, pickUpDate)) {
          // Geriye doğru seçim → Departure'ı güncelle
          setPickUpDate(triggerDate);
          setDropOffDate(pickUpDate);
        } else {
          // İleriye doğru seçim veya aynı gün → Return'ü set et
          setDropOffDate(triggerDate);
        }
        if (!isMobile) {
          dateSelectionRef.current?.close();
        }
      } else {
        // Her iki tarih de dolu → Yeni seçim başlat
        setPickUpDate(triggerDate);
        setDropOffDate(null);
      }
    },
    [setPickUpDate, setDropOffDate, pickUpDate, dropOffDate, isMobile],
  );

  // Web dropdown animasyon kontrolü
  useEffect(() => {
    if (isDifferentLocation) {
      // Web dropdown açılıyor
      setShouldRenderDropOffSection(true);
      // Küçük gecikme ile animasyonu tetikle
      const timer = setTimeout(() => {
        setIsDropOffSectionAnimating(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isDifferentLocation) {
      // Web dropdown kapanıyor
      setIsDropOffSectionAnimating(false);
      // Animasyon bitimini bekle, sonra DOM'dan kaldır
      const timer = setTimeout(() => {
        setShouldRenderDropOffSection(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isDifferentLocation]);

  // Render function for default content (when not searching)
  const renderDefaultContent = useCallback(
    ({ onSelectItem, onClosePortal, locationType }) => {
      return (
        <div className={"flex flex-col gap-5 text-sm"}>
          {latestCarSearches?.length > 0 && (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("carSearch.recentSearches")}
              </span>
              <div className="flex w-full flex-col items-start justify-between gap-4">
                {latestCarSearches.map((search, index) => (
                  <button
                    className="flex w-full cursor-pointer items-start justify-between gap-6"
                    onClick={() =>
                      handleSearchHistorySelect(
                        search,
                        locationType,
                        onClosePortal,
                      )
                    }
                    key={index}
                  >
                    <div className="flex items-center gap-4">
                      <FontAwesomeIcon
                        icon={byPrefixAndName.far["clock-rotate-left"]}
                      />
                      <div className="flex flex-col gap-1 text-left">
                        <div className="flex items-center justify-start gap-[6px] font-semibold">
                          <span>{search.location.value}</span>
                        </div>
                        <div className="text-foreground-muted">
                          {search.location.ParentLocationName}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Popular destinations */}
          {!popularLocations || popularLocations.length === 0 ? (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("carSearch.popularSearches")}
              </span>
              <div className="flex w-full flex-wrap gap-3">
                {/* Placeholder chips */}
                <span className="bg-background-400 h-8 w-24 animate-pulse rounded-lg" />
                <span className="bg-background-400 h-8 w-20 animate-pulse rounded-lg" />
                <span className="bg-background-400 h-8 w-28 animate-pulse rounded-lg" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("carSearch.popularSearches")}
              </span>
              <div className="flex w-full flex-wrap gap-3">
                {popularLocations.map((item, index) => (
                  <button
                    key={index}
                    className="border-border cursor-pointer rounded-lg border px-3 py-[8px] text-base"
                    onClick={() => onSelectItem(item)}
                  >
                    {item.Value}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    },
    [latestCarSearches, popularLocations, tForm, handleSearchHistorySelect],
  );

  // Render function for search results
  const renderSearchResults = useCallback(({ searchResults, onSelectItem }) => {
    if (searchResults?.length === 0) {
      return (
        <div className="flex flex-col gap-4 text-sm">
          <div className="flex flex-col items-center justify-center gap-3 text-sm md:p-10">
            <div className="flex flex-col items-center gap-2">
              <Image
                src={`${cdnCommonPath}telescope.svg`}
                alt="Alternatif Tarihler"
                width={60}
                height={60}
              />
              <span className="text-center text-lg font-semibold">
                {tForm("carSearch.noResultsFound")}
              </span>
              <span className="text-center text-sm">
                {tForm("carSearch.changeSearchCriteria")}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (searchResults?.length > 0) {
      return (
        <div className="flex flex-col gap-4 text-sm">
          {searchResults.map((searchResult) => (
            <button
              className="flex cursor-pointer items-start justify-between gap-6"
              key={searchResult.id}
              onClick={() => onSelectItem(searchResult)}
            >
              <div className="flex items-center gap-4 text-left">
                <FontAwesomeIcon
                  fontSize={18}
                  icon={byPrefixAndName.far[LOCATION_ICONS[searchResult.Type]]}
                />
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center justify-start gap-[6px] font-semibold">
                    {searchResult.Value}
                  </div>
                  <div className="text-foreground-muted">
                    {searchResult.ParentLocationName}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      );
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateAndSearch = useCallback(() => {
    const errors = validateForm();

    if (Object.keys(errors).length === 0) {
      // Validasyon başarılı - hatalar varsa temizle
      setValidationErrors({});

      // Son arama bilgilerini localStorage'a kaydet
      const newSearch = {
        location: pickUpLocation,
        pickupDateRaw: pickUpDate,
        pickupDate: pickUpDate?.toLocaleDateString("tr-TR"),
        dropOffDateRaw: dropOffDate,
        dropOffDate: dropOffDate?.toLocaleDateString("tr-TR"),
        pickUpTime,
        dropOffTime,
        driverAge,
      };

      // Mevcut arama geçmişini al
      const existingSearches = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || "[]",
      );

      const sameValueIndex = existingSearches.findIndex(
        (s) => s.location.Id === newSearch.location.Id,
      );

      if (sameValueIndex > -1) {
        existingSearches.splice(sameValueIndex, 1);
      }

      // Yeni aramayı array'in başına ekle
      const updatedSearches = [newSearch, ...existingSearches];

      // localStorage'a kaydet
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSearches));

      // URL oluştur ve yönlendir
      const url = generateCarSearchUrl();

      if (url) {
        const localizedPath = getPathname({
          href: url,
          locale,
        });

        if (onSearch) {
          onSearch();
        }

        window.location.href = localizedPath;
      }
    } else {
      // Validasyon başarısız - hataları göster
      setValidationErrors(errors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    validateForm,
    pickUpLocation,
    dropOffLocation,
    pickUpDate,
    dropOffDate,
    pickUpTime,
    dropOffTime,
    generateCarSearchUrl,
    driverAge,
    onSearch,
    locale,
  ]);

  const dropOffSectionClasses = useMemo(() => {
    return clsx(
      "[&_.web-dropdown]:scrollbar-hide w-full duration-300 max-md:overflow-hidden max-md:transition-[max-height] md:w-1/2 md:transform md:transition-all md:duration-200 md:ease-out [&_.web-dropdown]:max-h-[500px] [&_.web-dropdown]:overflow-auto",
      isDropOffSectionAnimating
        ? "max-md:max-h-[68px] md:translate-x-0 md:scale-100 md:opacity-100"
        : "max-md:max-h-0 md:-translate-x-4 md:scale-95 md:opacity-0",
      shouldRenderDropOffSection ? "" : "!w-0",
    );
  }, [isDropOffSectionAnimating, shouldRenderDropOffSection]);

  const pickupSectionClasses = useMemo(() => {
    return clsx(
      "[&_.web-dropdown]:scrollbar-hide w-full [&_.web-dropdown]:max-h-[500px] [&_.web-dropdown]:overflow-auto",
      shouldRenderDropOffSection ? "md:w-1/2" : "",
    );
  }, [shouldRenderDropOffSection]);

  /*
  const handleRemoveHistoryItem = (index) => {
    const existingSearches = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || "[]",
    );

    existingSearches.splice(index, 1);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existingSearches));
    getLatestHotelSearches();
  };
  */

  return (
    <div className={clsx("z-10 flex flex-col gap-4 md:gap-5", className || "")}>
      <div className="shadow-medium flex flex-col gap-0 rounded-xl bg-white md:gap-6 md:rounded-none md:bg-transparent md:shadow-none">
        <div className="flex w-full flex-col md:flex-row md:gap-4">
          <div className="md:shadow-medium flex flex-col items-center rounded-lg bg-white shadow-none md:w-4/4 md:flex-row">
            <div className="border-primary-weak relative z-[1] flex w-full items-center justify-between border-b px-[18px] py-3 md:hidden">
              <div className="text-primary-muted-subtext text-base font-normal">
                {t("carSearch.differentDropOffLocation")}
              </div>

              <button
                onClick={() => {
                  setIsDifferentLocation(!isDifferentLocation);
                }}
                className={clsx(
                  "relative h-6 w-11 rounded-full transition-colors",
                  isDifferentLocation ? "bg-primary" : "bg-gray-300",
                )}
              >
                <span
                  className={clsx(
                    "absolute top-0.5 left-0 h-5 w-5 rounded-full bg-white transition-transform",
                    isDifferentLocation ? "translate-x-5" : "translate-x-0.5",
                  )}
                />
              </button>
            </div>

            <div className="relative z-[1] flex w-full flex-1 flex-col items-end md:static md:flex-row md:items-center">
              {/* AutoComplete - Alış Lokasyonu */}
              <div className={pickupSectionClasses}>
                <GenericAutoComplete
                  id={`car-pickup-location_${id}`}
                  label={tForm("carSearch.pickUpLocation")}
                  searchFunction={searchForCarLocations}
                  onSelect={handlePickUpLocationSelect}
                  onClearSelection={clearPickUpLocationSelection}
                  value={pickUpLocation?.Value}
                  error={validationErrors.pickUpLocation}
                  translationNamespace="carSearch"
                  renderDefaultContent={renderDefaultContent}
                  onOpen={ensurePopularLoaded}
                  renderSearchResults={renderSearchResults}
                  inputClassName="font-semibold placeholder:font-medium placeholder:text-foreground-muted"
                  locationType="pickUpLocation"
                  customInputPlaceholder={tForm(
                    "carSearch.pickUpLocationPlaceholder",
                  )}
                  placeholderPortalInput={tForm(
                    "carSearch.pickUpLocationPortalPlaceholder",
                  )}
                  scrollToForm={scrollToForm}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                  webDropDownClassName="!mt-[5px]"
                  showCloseIconMobile
                  //renderNoResults={renderNoResults}
                  debounce={300}
                  minQueryLength={2}
                />
              </div>

              {shouldRenderDropOffSection && (
                <span className="h-[1px] w-4 bg-[var(--color-border)] max-md:hidden" />
              )}

              <div className={dropOffSectionClasses}>
                <GenericAutoComplete
                  id={`car-dropoff-location_${id}`}
                  label={tForm("carSearch.dropOffLocation")}
                  searchFunction={searchForCarLocations}
                  onSelect={handleDropOffLocationSelect}
                  onClearSelection={clearDropOffLocationSelection}
                  value={dropOffLocation?.Value}
                  error={validationErrors.dropOffLocation}
                  translationNamespace="carSearch"
                  renderDefaultContent={renderDefaultContent}
                  onOpen={ensurePopularLoaded}
                  renderSearchResults={renderSearchResults}
                  inputClassName="font-semibold"
                  locationType="dropOffLocation"
                  customInputPlaceholder={tForm(
                    "carSearch.dropOffLocationPlaceholder",
                  )}
                  placeholderPortalInput={tForm(
                    "carSearch.dropOffLocationPortalPlaceholder",
                  )}
                  scrollToForm={scrollToForm}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                  webDropDownClassName="!mt-[5px]"
                  showCloseIconMobile
                  //renderNoResults={renderNoResults}
                  debounce={300}
                  minQueryLength={2}
                />
              </div>
            </div>

            <span className="bg-border ml-0.5 hidden h-[34px] w-[1px] md:block" />

            {/* Date Selection */}
            <div className="border-primary-weak flex w-full flex-1 items-center md:max-w-max md:border-0">
              <div className="w-full md:max-w-max">
                <DateRangePicker
                  ref={dateSelectionRef}
                  id={`car-date_${id}`}
                  label={tForm("carSearch.dateSelection")}
                  onSelect={onSelectDropOffDate}
                  value={{
                    from: pickUpDate,
                    to: dropOffDate,
                  }}
                  disabled={pickUpDate}
                  onPortalStateChange={setIsDatePickerOpen}
                  defaultMonth={pickUpDate}
                  pickUpDate={pickUpDate}
                  dropOffDate={dropOffDate}
                  publicHolidays={publicHolidays}
                  setPickUpTime={setPickUpTime}
                  setDropOffTime={setDropOffTime}
                  pickUpTime={pickUpTime}
                  dropOffTime={dropOffTime}
                  onOpen={refreshPublicHolidays}
                  scrollToForm={scrollToForm}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                  webDropdownClassName={webDatePickerDropdownClassName}
                />
              </div>
            </div>

            {/* Driver Age Selection */}

            <div className="flex w-full items-center p-4 md:hidden">
              <DriverAgeSelect value={driverAge} onSelect={setDriverAge} />
            </div>
          </div>

          {/* Search Button */}
          <div className="p-3 max-md:pt-0 md:p-0">
            <button
              type="button"
              onClick={validateAndSearch}
              className="bg-primary hover:bg-primary/90 hover:shadow-soft inline-flex h-[50px] w-full cursor-pointer items-center justify-center gap-2 rounded-lg p-4 font-semibold text-white transition-all duration-200 ease-in-out md:h-[66px] md:w-max lg:text-lg"
            >
              {/* Yazı: Mobile ve Desktop'ta göster, Tablet'te gizle */}
              <span className="inline whitespace-nowrap md:hidden xl:inline">
                {tForm("Car.searchButton")}
              </span>

              {/* Turna icon: Mobile ve Desktop'ta göster, Tablet'te gizle */}
              <FontAwesomeIcon
                className="!hidden text-[28px] max-md:!inline-block xl:!inline-block"
                icon={byPrefixAndName.fak["turna-logo-icon"]}
              />

              {/* Büyüteç: Sadece Tablet'te göster */}
              <FontAwesomeIcon
                className="inline-block text-[28px] max-md:!hidden xl:!hidden"
                icon={byPrefixAndName.fas["magnifying-glass"]}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-1 flex w-full items-center justify-start gap-6 max-md:hidden">
        <div>
          <Checkbox
            size="lg"
            checked={isDifferentLocation}
            onChange={(e) => {
              setIsDifferentLocation(e.target.checked);
            }}
          >
            {t("carSearch.differentDropOffLocation")}
          </Checkbox>
        </div>

        <div>
          <DriverAgeSelect
            value={driverAge}
            onSelect={setDriverAge}
            scrollToForm={scrollToForm}
          />
        </div>
      </div>

      {/*!hideLatestSearches && latestHotelSearches?.length > 0 && (
        <div className="flex flex-col gap-4 md:mt-10">
          <div className="hidden font-semibold md:block">Son Aramalar</div>

          <div className="flex gap-4 overflow-auto">
            {latestHotelSearches.map((search, index) => (
              <div
                className="flex items-center rounded-[10px] bg-white"
                key={index}
              >
                <button
                  onClick={() => handleSearchHistorySelectSearch(search)}
                  key={index}
                  className="flex items-center gap-3 py-3 pr-2 pl-3"
                >
                  <FontAwesomeIcon
                    className="text-primary"
                    fontSize={16}
                    icon={byPrefixAndName.fak["new-hotel"]}
                  />

                  <div className="flex flex-col gap-1 text-left">
                    <div className="flex items-center justify-start gap-[6px] text-sm whitespace-nowrap">
                      <span className="max-w-[92px] truncate">
                        {search.location.value.split("-")[0].trim()}
                      </span>
                      -<span>{getGuestInfoText(search?.guests)}</span>
                    </div>
                  </div>
                </button>

                <button
                  className="flex items-center pr-3"
                  onClick={() => handleRemoveHistoryItem(index)}
                >
                  <FontAwesomeIcon
                    className="text-[#909294]"
                    fontSize={16}
                    icon={byPrefixAndName.far["xmark"]}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      )*/}
    </div>
  );
};

export default Form;
